import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Linkedin, Shield, TrendingUp, Users, AlertCircle, ArrowRight, Mail, Lock, CheckCircle2, Star, Phone, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { PhoneInput } from "@/components/ui/phone-input";
import { trackFacebookEvent } from "@/utils/facebookTracking";
import { useBranding } from "@/hooks/useBranding";

// Window interface declarations are in vite-env.d.ts

const signUpSchema = z.object({
  organizationName: z.string().trim().min(2, "Organization name must be at least 2 characters").max(100, "Organization name must be less than 100 characters"),
  fullName: z.string().trim().min(2, "Name must be at least 2 characters"),
  email: z.string().trim().email("Invalid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  phoneNumber: z.string().min(6, "Phone number must be at least 6 digits"),
});

const signInSchema = z.object({
  email: z.string().trim().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function Auth() {
  const { user, signUp, signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brand = useBranding();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inviteToken = searchParams.get("invite");

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  // Signup success state - show email confirmation screen
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState("");

  // Resend verification email state
  const [resendCooldown, setResendCooldown] = useState(0);

  // Rate limiting state
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);

  // Already registered state
  const [alreadyRegisteredEmail, setAlreadyRegisteredEmail] = useState<string | null>(null);

  // Email not confirmed state (for sign-in failures)
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState("");

  // Active tab state for controlled Tabs
  const [activeTab, setActiveTab] = useState("signin");

  // Password visibility state
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);

  const [signUpData, setSignUpData] = useState({
    organizationName: "",
    fullName: "",
    email: "",
    password: "",
    phoneCountryCode: "+91",
    phoneNumber: "",
  });

  const [signInData, setSignInData] = useState({
    email: "",
    password: "",
  });

  // Capture tracking parameters on mount
  useEffect(() => {
    const params: Record<string, string> = {};
    const trackingKeys = ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'];
    trackingKeys.forEach(key => {
      const val = searchParams.get(key);
      if (val) params[key] = val;
    });
    if (Object.keys(params).length > 0) {
      try { sessionStorage.setItem('tracking_params', JSON.stringify(params)); } catch { /* ignore */ }
    }
  }, [searchParams]);

  useEffect(() => {
    if (user && inviteToken) {
      navigate(`/invite/${inviteToken}`);
    } else if (user) {
      // Verification checks disabled — go straight to dashboard/onboarding
      // RoleBasedLayout will redirect to /onboarding if workspace setup is incomplete
      navigate("/");
    }
  }, [user, navigate, inviteToken]);

  // Check if user needs mobile verification (India only)
  const isIndiaUser = signUpData.phoneCountryCode === "+91";

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setAlreadyRegisteredEmail(null);

    try {
      signUpSchema.parse(signUpData);
      await proceedWithSignUp();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else if (error?.message?.toLowerCase().includes("already registered")) {
        setAlreadyRegisteredEmail(signUpData.email);
      }
    }
  };

  const proceedWithSignUp = async () => {
    setIsLoading(true);
    try {
      const result = await signUp({
        email: signUpData.email,
        password: signUpData.password,
        fullName: signUpData.fullName,
        phoneCountryCode: signUpData.phoneCountryCode,
        phoneNumber: signUpData.phoneNumber,
        organizationName: signUpData.organizationName,
      });

      if (result.success) {
        // Track Facebook CompleteRegistration event
        const nameParts = signUpData.fullName.trim().split(' ');
        trackFacebookEvent('CompleteRegistration', {
          email: signUpData.email,
          firstName: nameParts[0],
          lastName: nameParts.slice(1).join(' ') || undefined,
          phone: signUpData.phoneCountryCode + signUpData.phoneNumber,
        }, {
          content_name: 'User Registration Complete',
        });

        if (result.needsEmailConfirmation) {
          setSignUpEmail(signUpData.email);
          setSignUpSuccess(true);
        } else {
          // Navigate to onboarding for verification and workspace setup
          navigate("/onboarding");
        }
      }
    } catch (error: any) {
      if (error?.message?.toLowerCase().includes("already registered")) {
        setAlreadyRegisteredEmail(signUpData.email);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Check if user is locked out
    if (lockoutUntil && new Date() < lockoutUntil) {
      const remainingSeconds = Math.ceil((lockoutUntil.getTime() - Date.now()) / 1000);
      toast.error(`Too many attempts. Please wait ${remainingSeconds} seconds.`);
      return;
    }

    try {
      signInSchema.parse(signInData);
      setIsLoading(true);
      // Reset email not confirmed state
      setEmailNotConfirmed(false);
      setUnconfirmedEmail("");

      const success = await signIn(signInData.email, signInData.password);

      if (success) {
        // Reset on successful login
        setFailedAttempts(0);
        setLockoutUntil(null);
      } else {
        // Increment failed attempts
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);

        // Lock out after 5 failed attempts for 30 seconds
        if (newAttempts >= 5) {
          const lockoutTime = new Date(Date.now() + 30000);
          setLockoutUntil(lockoutTime);
          toast.error("Too many failed attempts. Please wait 30 seconds.");

          // Auto-reset lockout after time expires
          setTimeout(() => {
            setLockoutUntil(null);
            setFailedAttempts(0);
          }, 30000);
        }
      }
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else if (error.message === "EMAIL_NOT_CONFIRMED") {
        // Show resend verification UI
        setEmailNotConfirmed(true);
        setUnconfirmedEmail(signInData.email);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async (emailToResend?: string) => {
    if (resendCooldown > 0) return;

    // Use provided email, or signUpEmail, or unconfirmedEmail
    const targetEmail = emailToResend || signUpEmail || unconfirmedEmail;
    if (!targetEmail) {
      toast.error("No email address to resend verification to");
      return;
    }

    try {
      setIsLoading(true);
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: targetEmail,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) throw error;

      toast.success("Verification email sent!");
      setResendCooldown(60);

      // Countdown
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast.error("Failed to resend: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error("Please enter your email address");
      return;
    }

    // Validate email format
    const emailSchema = z.string().trim().email("Invalid email address");
    try {
      emailSchema.parse(resetEmail);
    } catch (error) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      // Even if there's a timeout error, the email is sent in background
      if (error && !error.message.includes('timeout') && !error.message.includes('5.000000 seconds')) {
        throw error;
      }

      setResetSent(true);
      toast.success("If an account exists, you'll receive a password reset email.");
    } catch (error: any) {
      // Check if it's a timeout error - email is still sent in background
      if (error.message?.includes('timeout') || error.message?.includes('5.000000 seconds')) {
        setResetSent(true);
        toast.success("If an account exists, you'll receive a password reset email.");
      } else {
        toast.error("Failed to send reset email: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const closeForgotPasswordDialog = () => {
    setShowForgotPassword(false);
    setResetSent(false);
    setResetEmail("");
  };

  return (
    <div className="min-h-[100dvh] w-full flex bg-background">
      {/* Left Side - Brand & Features (Hidden on Mobile) */}
      <div className="hidden lg:flex w-1/2 relative bg-white text-brand-950 overflow-hidden flex-col justify-between p-16 border-r border-brand-100">
        {/* Background Gradients - Sophisticated Mesh (Light Mode) */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-brand-100/40 via-white to-white z-0"></div>
        <div className="absolute top-[-10%] right-[-20%] w-[700px] h-[700px] bg-brand-200/20 rounded-full blur-[120px] z-0 mix-blend-multiply animate-pulse duration-10000"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-200/20 rounded-full blur-[100px] z-0 opacity-70 mix-blend-multiply"></div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.02] z-0 mix-blend-multiply"></div>

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <img
            src={brand.logoLight}
            alt={brand.productName}
            className="h-10 w-auto object-contain"
          />
          {(
            <span className="text-xl font-heading font-bold tracking-tight text-brand-900">{brand.productName}</span>
          )}
        </div>

        {/* Main Content - Minimal & Trusted */}
        <div className="relative z-10 max-w-xl">
          <h1 className="text-6xl font-heading font-bold leading-[1.1] tracking-tight mb-8 text-brand-950">
            Grow your network,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-700 via-brand-600 to-brand-700 animate-gradient-x">effortlessly.</span>
          </h1>

          <p className="text-lg text-brand-900/80 leading-relaxed font-medium max-w-md mb-12">
            The <span className="text-brand-950 font-bold">easiest cloud-based tool</span> for scaling your LinkedIn outreach with human-like precision.
          </p>

          {/* Floating Glass Card - Social Proof (WDC main domain only) */}
          {(
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-500/20 to-teal-accent-500/20 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
              <div className="relative flex items-center gap-5 p-5 rounded-2xl bg-white/70 border border-brand-100 shadow-sm backdrop-blur-xl hover:bg-white/90 transition-colors">
                <div className="flex -space-x-2 shrink-0">
                  {[
                    { name: "Tata", src: "/logos/tata.png" },
                    { name: "Emami", src: "/logos/emami.png" },
                    { name: "Slack", src: "/logos/slack.svg" },
                    { name: "Volkswagen", src: "/logos/volkswagen.png" }
                  ].map((logo, index) => (
                    <div
                      key={logo.name}
                      className={`relative w-10 h-10 rounded-full border-2 border-white bg-white flex items-center justify-center ring-2 ring-brand-50 overflow-hidden ${logo.name === 'Emami' ? 'p-0.5' : 'p-1.5'}`}
                      style={{ zIndex: 10 - index }}
                    >
                      <img
                        src={logo.src}
                        alt={logo.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
                <div className="h-8 w-px bg-zinc-200"></div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-brand-700/70 font-medium">
                    Loved by <span className="text-brand-900 font-bold">2,000+</span> growth teams
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Minimal Badge */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 border border-brand-100 text-xs font-medium text-brand-600">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-accent-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-accent-500"></span>
            </span>
            System Operational
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12 bg-background overflow-y-auto">
        <div className="w-full max-w-[400px] space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 my-auto">

          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-2">
            <img
              src={brand.logoLight}
              alt={brand.productName}
              className="h-12 w-auto object-contain"
            />
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-brand-950">Welcome back</h2>
            <p className="text-muted-foreground">
              Enter your credentials to access your workspace
            </p>
          </div>

          <div className="w-full">
            <div className="space-y-6 focus-visible:ring-0 mt-2">
              <form onSubmit={handleSignIn} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="signin-email" className="text-sm font-medium ml-1">Email Address</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="name@work-email.com"
                    value={signInData.email}
                    onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                    className={`h-10 px-3 bg-background border-input focus:bg-background transition-all ${errors.email ? "border-destructive focus-visible:ring-destructive" : ""}`}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password" className="text-sm font-medium ml-1">Password</Label>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-xs font-medium text-primary hover:text-primary/80"
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <div className="relative">
                    <Input
                      id="signin-password"
                      type={showSignInPassword ? "text" : "password"}
                      value={signInData.password}
                      onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                      className={`h-10 px-3 pr-10 bg-background border-input focus:bg-background transition-all ${errors.password ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowSignInPassword(!showSignInPassword)}
                    >
                      {showSignInPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.password}
                    </p>
                  )}
                </div>

                {lockoutUntil && new Date() < lockoutUntil && (
                  <div className="flex items-start gap-3 p-4 bg-destructive/5 border border-destructive/10 rounded-lg text-sm text-destructive">
                    <Lock className="w-5 h-5 shrink-0 mt-0.5" />
                    <span className="font-medium">Too many attempts. Please wait before trying again.</span>
                  </div>
                )}

                {emailNotConfirmed && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-lg space-y-3">
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Email not verified</p>
                        <p className="text-xs text-amber-700 dark:text-amber-300/80">
                          Please check your inbox for <span className="font-medium">{unconfirmedEmail}</span> and click the verification link.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleResendVerification(unconfirmedEmail)}
                      disabled={resendCooldown > 0 || isLoading}
                      className="w-full border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Verification Email"}
                    </Button>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-10 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 mt-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white border-0"
                  disabled={isLoading || (lockoutUntil !== null && new Date() < lockoutUntil)}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <span className="flex items-center gap-2">
                      Sign In <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Mobile Social Proof & Logos (WDC main domain only) */}
          {(
            <div className="lg:hidden pt-6 border-t border-border/40 text-center space-y-6">
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Trusted by high-growth teams
                </p>
                <div className="flex items-center justify-center gap-10 opacity-60 grayscale">
                  {[
                    { name: "Tata", src: "/logos/tata.png" },
                    { name: "Emami", src: "/logos/emami.png" },
                    { name: "Volkswagen", src: "/logos/volkswagen.png" }
                  ].map((logo) => (
                    <div key={logo.name} className="flex items-center justify-center">
                      <img
                        src={logo.src}
                        alt={logo.name}
                        className="h-8 w-auto object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPasswordDialog}>
        <DialogContent className="sm:max-w-[400px] rounded-2xl p-0 overflow-hidden border-none shadow-xl">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-center tracking-tight">Reset Password</DialogTitle>
              <DialogDescription className="text-center text-muted-foreground mt-2 max-w-[85%] mx-auto">
                Enter your email address and we'll send you a link to reset your password.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6 pt-6">
            {!resetSent ? (
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm font-medium ml-1">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="name@work-email.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="h-10 rounded-md bg-background border-input"
                  />
                </div>
                <Button
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  className="w-full h-10 font-medium shadow-sm"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center space-y-6 py-2">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-2">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 shadow-sm">
                    <Mail className="w-6 h-6" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-foreground">Check your email</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed px-4">
                    If an account exists for <span className="font-semibold text-foreground">{resetEmail}</span>, you'll receive a password reset link shortly.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={closeForgotPasswordDialog}
                  className="w-full h-10 rounded-md border-input hover:bg-muted"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
